import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Icon } from "@iconify/react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CenterType from "../../../components/CenterType";

const COLUMNS_STORAGE_KEY = "ecg-reports:list-columns";

const getReportId = (report) => report?.report?.id || report?.id;
const getPatient = (report) => report?.patient_details || report?.user_data || {};

const getPatientName = (report) => {
  const patient = getPatient(report);
  return [patient?.firstName || patient?.first_name, patient?.lastName || patient?.last_name]
    .filter(Boolean)
    .join(" ") || "--";
};

const formatCollectedOn = (report) => {
  const rawDate = report?.report_timestamp_readable || report?.report_timestamp || report?.createdAt;
  if (!rawDate) return "--";
  const date = typeof rawDate === "number" || /^\d+$/.test(String(rawDate))
    ? moment(Number(rawDate))
    : moment(rawDate);
  return date.isValid() ? date.format("DD MMM YYYY, hh:mm A") : "--";
};

function ReportsListView({
  reports,
  isLoading,
  totalCount,
  hasMore,
  listRef,
  onScroll,
  openedMap,
  onExport,
  onViewReport,
  onDownloadReport,
  canDownloadAIReport,
  onDownloadAIReport,
  onSendForInterpretation,
  isMigratedUser
}) {
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [activeReportIndex, setActiveReportIndex] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = sessionStorage.getItem(COLUMNS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : { patient: true, patientId: false, centre: true, testType: false };
  });

  useEffect(() => {
    sessionStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const columns = useMemo(() => [
    {
      key: "collectionId",
      label: "Collection ID",
      required: true,
      render: (report) => <Typography sx={{ fontSize: 13, fontWeight: 800, whiteSpace: "nowrap" }}>#{report?.collection_id || "--"}</Typography>,
    },
    {
      key: "patient",
      label: "Patient",
      render: (report) => {
        const patient = getPatient(report);
        const age = patient?.age || patient?.userAge;
        return <Box sx={{ minWidth: 150 }}><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{getPatientName(report)}</Typography><Typography sx={{ color: "var(--app-text-muted)", fontSize: 11 }}>{[age, patient?.gender].filter(Boolean).join(" · ") || "Patient details unavailable"}</Typography></Box>;
      },
    },
    {
      key: "centre",
      label: "Centre",
      render: (report) => {
        const centerName = report?.centerName || report?.center_details?.centerName || report?.centre_details?.centerName;
        const centerType = report?.centerType || report?.center_details?.centerType || report?.centre_details?.centerType;
        return centerName ? <CenterType data={{ ...report, centerName, centerType }} /> : <Typography sx={{ color: "var(--app-text-muted)", fontSize: 13 }}>--</Typography>;
      },
    },
    {
      key: "collectedOn",
      label: "Collected on",
      required: true,
      render: (report) => <Typography sx={{ color: "var(--app-text-muted)", fontSize: 13, whiteSpace: "nowrap" }}>{formatCollectedOn(report)}</Typography>,
    },
    {
      key: "interpretation",
      label: "Interpretation",
      required: true,
      render: (report) => {
        const status = report?.interpretation_request_status || "not interpreted";
        const completed = status === "completed";
        const awaiting = status === "requested" || status === "in progress";
        const skipped = status === "skipped_no_credits";
        const label = skipped ? "Skipped — no credits" : status.replaceAll("_", " ");
        return <Box sx={{ display: "inline-flex", px: 1, py: 0.4, borderRadius: 12, bgcolor: completed ? "var(--app-success-bg)" : (awaiting || skipped) ? "var(--app-warning-bg)" : "var(--app-danger-bg)", color: completed ? "var(--app-success)" : (awaiting || skipped) ? "var(--app-warning)" : "var(--app-danger)", fontSize: 11, fontWeight: 800, textTransform: "capitalize", whiteSpace: "nowrap" }}>{label}</Box>;
      },
    },
    {
      key: "result",
      label: "Result",
      required: true,
      render: (report) => {
        const result = (report?.interpretation_report_status || report?.report_status || "--").replaceAll("_", " ");
        const critical = /critical/i.test(result);
        const normal = /normal/i.test(result);
        return <Typography sx={{ color: critical ? "var(--app-danger)" : normal ? "var(--app-success)" : "var(--app-warning)", fontSize: 13, fontWeight: 800, textTransform: "capitalize", whiteSpace: "nowrap" }}>{result}</Typography>;
      },
    },
  ], []);

  const activeColumns = columns.filter((column) => column.required || visibleColumns[column.key]);
  const closeActions = () => {
    setActionAnchorEl(null);
    setActiveReport(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Typography
          sx={{
            color: "var(--app-text)",
            fontFamily: "Figtree",
            fontSize: 20,
            fontWeight: 700,
          }}
        >
          Reports List{" "}
          <Box
            component="span"
            sx={{
              color: "var(--app-text-muted)",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            ({totalCount})
          </Box>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

          <Button size="small" variant="outlined" onClick={(event) => setColumnAnchorEl(event.currentTarget)} startIcon={<Icon icon="material-symbols:view-column-outline-rounded" width="17" height="17" />} sx={{ minHeight: 32, borderColor: "var(--app-border)", borderRadius: 2, color: "var(--app-text)", fontSize: 12, fontWeight: 700, textTransform: "none" }}>Columns</Button>
          <Button size="small" variant="text" onClick={onExport} disabled={!totalCount} startIcon={<Icon icon="material-symbols:download" width="18" height="18" />} sx={{ minHeight: 32, color: "var(--app-accent)", fontSize: 12, fontWeight: 700, textTransform: "none" }}>Export CSV</Button>
        </Box>
      </Box>

      <Menu anchorEl={columnAnchorEl} open={Boolean(columnAnchorEl)} onClose={() => setColumnAnchorEl(null)} PaperProps={{ sx: { mt: 1, minWidth: 205, border: "1px solid var(--app-border)", borderRadius: 2, boxShadow: "var(--app-shadow)" } }}>
        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, color: "var(--app-text-muted)", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>Optional columns</Typography>
        {columns.filter((column) => !column.required).map((column) => <MenuItem key={column.key} dense onClick={() => setVisibleColumns((current) => ({ ...current, [column.key]: !current[column.key] }))}><Checkbox size="small" checked={visibleColumns[column.key]} sx={{ py: 0.25, pr: 1 }} /><ListItemText primary={column.label} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} /></MenuItem>)}
      </Menu>

      <TableContainer ref={listRef} onScroll={onScroll} sx={{ maxHeight: "68vh", overflow: "auto" }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1100, borderCollapse: "separate", borderSpacing: "0 10px", "& .MuiTableCell-root": { borderBottom: 0 } }} aria-label="ECG reports">
          <TableHead>
            <TableRow>
              {activeColumns.map((column) => <TableCell key={column.key} sx={{ backgroundColor: "var(--app-surface-2)", color: "#A3A3A3", fontFamily: "Figtree", fontSize: "15px", fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", whiteSpace: "nowrap", borderBottom: "none", py: 1.5 }}>{column.label}</TableCell>)}
              <TableCell align="center" sx={{ backgroundColor: "var(--app-surface-2)", color: "#A3A3A3", fontFamily: "Figtree", fontSize: "15px", fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", borderBottom: "none", py: 1.5 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && reports.length === 0 ? [1, 2, 3, 4, 5].map((row) => <TableRow key={row}><TableCell colSpan={activeColumns.length + 1}><Skeleton variant="rounded" height={46} animation="wave" /></TableCell></TableRow>) : reports.map((report, index) => {
              const isOpened = openedMap[getReportId(report)] ?? report?.isOpened;
              return <TableRow key={report._id || getReportId(report)} hover sx={{ cursor: "pointer", "& td": { background: isOpened ? "var(--app-surface)" : "var(--app-unread)", py: 1.8, transition: "background .2s" }, "& td:first-of-type": { borderLeft: "1px solid var(--app-border)", borderTopLeftRadius: 2.5, borderBottomLeftRadius: 2.5 }, "& td:last-of-type": { borderRight: "1px solid var(--app-border)", borderTopRightRadius: 2.5, borderBottomRightRadius: 2.5 }, "&:hover td": { background: "var(--app-hover)" } }}>
                {activeColumns.map((column) => <TableCell key={column.key}>{column.render(report)}</TableCell>)}
                <TableCell align="center"><IconButton size="small" aria-label="Report actions" onClick={(event) => { setActionAnchorEl(event.currentTarget); setActiveReport(report); setActiveReportIndex(index); }} sx={{ width: 34, height: 34, borderRadius: 2 }}><Icon icon="material-symbols:more-vert" width="18" /></IconButton></TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={closeActions}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 185,
            border: "1px solid var(--app-border)",
            borderRadius: 2,
            boxShadow: "var(--app-shadow)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onViewReport(activeReport, activeReportIndex);
            closeActions();
          }}
        >
          <Icon
            icon="material-symbols:visibility-outline-rounded"
            width="18"
            height="18"
            style={{ marginRight: 10 }}
          />
          <ListItemText
            primary="View report"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            onDownloadReport(activeReport);
            closeActions();
          }}
        >
          <Icon
            icon="material-symbols:download-rounded"
            width="18"
            height="18"
            style={{ marginRight: 10 }}
          />
          <ListItemText
            primary="Download report"
            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
          />
        </MenuItem>

        {canDownloadAIReport && (
          <MenuItem
            onClick={() => {
              onDownloadAIReport(activeReport);
              closeActions();
            }}
          >
            <Icon
              icon="material-symbols:download-rounded"
              width="18"
              height="18"
              style={{ marginRight: 10 }}
            />
            <ListItemText
              primary="Download AI Generated Report"
              primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
            />
          </MenuItem>
        )}

        {activeReport &&
          !isMigratedUser &&
          !["requested", "in progress", "completed"].includes(
            activeReport.interpretation_request_status
          ) && (
            <MenuItem
              onClick={() => {
                onSendForInterpretation(activeReport);
                closeActions();
              }}
            >
              <Icon
                icon="material-symbols:clinical-notes-outline-rounded"
                width="18"
                height="18"
                style={{ marginRight: 10 }}
              />
              <ListItemText
                primary="Send For Interpretation"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
              />
            </MenuItem>
          )}
      </Menu>

      {isLoading && reports.length > 0 && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>}
      {!hasMore && reports.length > 0 && <Typography textAlign="center" variant="body2" color="text.secondary" sx={{ py: 2 }}>No more reports</Typography>}
    </Box>
  );
}

export default ReportsListView;

ReportsListView.propTypes = {
  reports: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  listRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  onScroll: PropTypes.func.isRequired,
  openedMap: PropTypes.object.isRequired,
  onExport: PropTypes.func.isRequired,
  onViewReport: PropTypes.func.isRequired,
  onDownloadReport: PropTypes.func.isRequired,
  canDownloadAIReport: PropTypes.bool,
  onDownloadAIReport: PropTypes.func,
  onSendForInterpretation: PropTypes.func.isRequired,
  isMigratedUser: PropTypes.bool,
};
